import React, { useState, useContext, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardBody, Col, Container, Form, Input, Label, Row, FormFeedback, CardHeader } from "reactstrap";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PocketBaseContext } from '../../../PocketBaseContext';
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Dropzone from "react-dropzone";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { toast } from "react-toastify";

const ProductAdd = () => {
  document.title = "Products | Reform Panel";
  const navigate = useNavigate();
  const pb = useContext(PocketBaseContext);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const records = await pb.collection('supplier').getFullList({
        sort: 'supplier_name',
      });
      setSupplierOptions(records.map(supplier => ({
        value: supplier.id,
        label: supplier.supplier_name
      })));
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const validation = useFormik({
    initialValues: {
      product_name: "",
      product_code: "",
      stock_quantity: "",
      kdv_vat_rate: "",
      currency: "TL",
      purchase_price_excl_vat: "",
      purchase_price_incl_vat: "",
      sales_price_excl_vat: "",
      sales_price_incl_vat: "",
      description: "",
      additional_information: "",
      supplier: "",
    },
    validationSchema: Yup.object({
      product_name: Yup.string().required("Ürün adı zorunludur"),
      product_code: Yup.string().required("Ürün kodu zorunludur"),
      stock_quantity: Yup.number().required("Stok miktarı zorunludur"),
      kdv_vat_rate: Yup.number().required("KDV oranı zorunludur"),
      currency: Yup.string().required("Para birimi zorunludur"),
      purchase_price_excl_vat: Yup.number().required("KDV hariç alış fiyatı zorunludur"),
      purchase_price_incl_vat: Yup.number().required("KDV dahil alış fiyatı zorunludur"),
      sales_price_excl_vat: Yup.number().required("KDV hariç satış fiyatı zorunludur"),
      sales_price_incl_vat: Yup.number().required("KDV dahil satış fiyatı zorunludur")
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();

        Object.keys(values).forEach(key => {
          if (values[key] !== null && values[key] !== undefined) {
            formData.append(key, values[key]);
          }
        });

        if (selectedSupplier) {
          formData.append("supplier", selectedSupplier.value);
        }

        if (selectedFiles.length > 0) {
          formData.append("picture", selectedFiles[0].file, selectedFiles[0].name);
        }

        const record = await pb.collection('products').create(formData);
        toast.success("Ürün başarıyla eklendi");
        navigate("/reform-products/list");
      } catch (error) {
        console.error("Error adding product:", error);
        toast.error("Ürün eklenirken bir hata oluştu. Lütfen tekrar deneyin.");
      }
    }
  });

  // Dropzone için dosya tipi ve boyut kısıtlamaları
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  // handleAcceptedFiles fonksiyonunu güncelleyelim
  const handleAcceptedFiles = (acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file => {
      // Dosya tipi ve boyut kontrolü
      const isValidType = ALLOWED_FILE_TYPES.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE;

      if (!isValidType) {
        toast.warning('Sadece JPEG, PNG, GIF ve WEBP dosyaları kabul edilmektedir.');
        return false;
      }
      if (!isValidSize) {
        toast.warning('Dosya boyutu 5MB\'dan küçük olmalıdır.');
        return false;
      }

      return true;
    });

    // Keep original file objects for FormData, create previews separately
    const filesWithPreviews = validFiles.map(file => ({
      file: file, // Original file for FormData
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
      name: file.name
    }));

    setSelectedFiles(filesWithPreviews);
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Add Product" pageTitle="Products" />
          <Form onSubmit={validation.handleSubmit}>
            <Row>
              <Col lg={8}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Ürün Bilgileri</h4>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3 position-relative">
                          <Label htmlFor="product_name" className="form-label">Ürün Adı</Label>
                          <Input
                            type="text"
                            className="form-control"
                            id="product_name"
                            placeholder="Enter product name"
                            {...validation.getFieldProps('product_name')}
                            invalid={validation.touched.product_name && validation.errors.product_name}
                          />
                          {validation.touched.product_name && validation.errors.product_name && (
                            <FormFeedback type="invalid" tooltip>{validation.errors.product_name}</FormFeedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3 position-relative">
                          <Label htmlFor="product_code" className="form-label">Ürün Kodu</Label>
                          <Input
                            type="text"
                            className="form-control"
                            id="product_code"
                            placeholder="Enter product code"
                            {...validation.getFieldProps('product_code')}
                            invalid={validation.touched.product_code && validation.errors.product_code}
                          />
                          {validation.touched.product_code && validation.errors.product_code && (
                            <FormFeedback type="invalid" tooltip>{validation.errors.product_code}</FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <div className="mb-3">
                          <Label htmlFor="description" className="form-label">Ürün Açıklaması</Label>
                          <CKEditor
                            editor={ClassicEditor}
                            data={validation.values.description}
                            onChange={(event, editor) => {
                              validation.setFieldValue('description', editor.getData());
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <div className="mb-3">
                          <Label className="form-label">Ürün Resmi</Label>
                          <Dropzone
                            onDrop={handleAcceptedFiles}
                            accept={{
                              'image/*': ALLOWED_FILE_TYPES
                            }}
                            maxSize={MAX_FILE_SIZE}
                            multiple={false}
                          >
                            {({ getRootProps, getInputProps, isDragActive }) => (
                              <div className={`dropzone dz-clickable ${isDragActive ? 'dz-drag-hover' : ''}`} {...getRootProps()}>
                                <input {...getInputProps()} />
                                <div className="dz-message needsclick">
                                  <div className="mb-3">
                                    <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                  </div>
                                  <h4>{isDragActive ? "Dosyayı buraya bırakın" : "Dosya yüklemek için tıklayın veya sürükleyin"}</h4>
                                  <span className="badge border border-danger text-danger">
                                    Maksimum dosya boyutu: 5MB (JPEG, PNG, GIF, WEBP)
                                  </span>
                                </div>
                              </div>
                            )}
                          </Dropzone>
                          <div className="list-unstyled mb-0" id="file-previews">
                            {selectedFiles.map((f, i) => (
                              <Card
                                className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                key={i + "-file"}
                              >
                                <div className="p-2">
                                  <Row className="align-items-center">
                                    <Col className="col-auto">
                                      <img
                                        data-dz-thumbnail=""
                                        height="80"
                                        className="avatar-sm rounded bg-light"
                                        alt={f.name}
                                        src={f.preview}
                                      />
                                    </Col>
                                    <Col>
                                      <Link
                                        to="#"
                                        className="text-muted font-weight-bold"
                                      >
                                        {f.name}
                                      </Link>
                                      <p className="mb-0">
                                        <strong>{f.formattedSize}</strong>
                                      </p>
                                    </Col>
                                  </Row>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Fiyatlandırma</h4>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3 position-relative">
                          <Label htmlFor="purchase_price_excl_vat" className="form-label">Alış Fiyatı (KDV Hariç)</Label>
                          <Input
                            type="number"
                            className="form-control"
                            id="purchase_price_excl_vat"
                            placeholder="Enter purchase price"
                            {...validation.getFieldProps('purchase_price_excl_vat')}
                            invalid={validation.touched.purchase_price_excl_vat && validation.errors.purchase_price_excl_vat}
                          />
                          {validation.touched.purchase_price_excl_vat && validation.errors.purchase_price_excl_vat && (
                            <FormFeedback type="invalid" tooltip>{validation.errors.purchase_price_excl_vat}</FormFeedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3 position-relative">
                          <Label htmlFor="purchase_price_incl_vat" className="form-label">Alış Fiyatı (KDV Dahil)</Label>
                          <Input
                            type="number"
                            className="form-control"
                            id="purchase_price_incl_vat"
                            placeholder="Enter purchase price"
                            {...validation.getFieldProps('purchase_price_incl_vat')}
                            invalid={validation.touched.purchase_price_incl_vat && validation.errors.purchase_price_incl_vat}
                          />
                          {validation.touched.purchase_price_incl_vat && validation.errors.purchase_price_incl_vat && (
                            <FormFeedback type="invalid" tooltip>{validation.errors.purchase_price_incl_vat}</FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3 position-relative">
                          <Label htmlFor="sales_price_excl_vat" className="form-label">Satış Fiyatı (KDV Hariç)</Label>
                          <Input
                            type="number"
                            className="form-control"
                            id="sales_price_excl_vat"
                            placeholder="Enter sales price"
                            {...validation.getFieldProps('sales_price_excl_vat')}
                            invalid={validation.touched.sales_price_excl_vat && validation.errors.sales_price_excl_vat}
                          />
                          {validation.touched.sales_price_excl_vat && validation.errors.sales_price_excl_vat && (
                            <FormFeedback type="invalid" tooltip>{validation.errors.sales_price_excl_vat}</FormFeedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3 position-relative">
                          <Label htmlFor="sales_price_incl_vat" className="form-label">Satış Fiyatı (KDV Dahil)</Label>
                          <Input
                            type="number"
                            className="form-control"
                            id="sales_price_incl_vat"
                            placeholder="Enter sales price"
                            {...validation.getFieldProps('sales_price_incl_vat')}
                            invalid={validation.touched.sales_price_incl_vat && validation.errors.sales_price_incl_vat}
                          />
                          {validation.touched.sales_price_incl_vat && validation.errors.sales_price_incl_vat && (
                            <FormFeedback type="invalid" tooltip>{validation.errors.sales_price_incl_vat}</FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Stok Bilgileri</h4>
                    <div className="mb-3 position-relative">
                      <Label htmlFor="stock_quantity" className="form-label">Stok Miktarı</Label>
                      <Input
                        type="number"
                        className="form-control"
                        id="stock_quantity"
                        placeholder="Enter stock quantity"
                        {...validation.getFieldProps('stock_quantity')}
                        invalid={validation.touched.stock_quantity && validation.errors.stock_quantity}
                      />
                      {validation.touched.stock_quantity && validation.errors.stock_quantity && (
                        <FormFeedback type="invalid" tooltip>{validation.errors.stock_quantity}</FormFeedback>
                      )}
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">KDV ve Para Birimi</h4>
                    <div className="mb-3 position-relative">
                      <Label htmlFor="kdv_vat_rate" className="form-label">KDV Oranı (%)</Label>
                      <Input
                        type="number"
                        className="form-control"
                        id="kdv_vat_rate"
                        placeholder="Enter VAT rate"
                        {...validation.getFieldProps('kdv_vat_rate')}
                        invalid={validation.touched.kdv_vat_rate && validation.errors.kdv_vat_rate}
                      />
                      {validation.touched.kdv_vat_rate && validation.errors.kdv_vat_rate && (
                        <FormFeedback type="invalid" tooltip>{validation.errors.kdv_vat_rate}</FormFeedback>
                      )}
                    </div>
                    <div className="mb-3 position-relative">
                      <Label htmlFor="currency" className="form-label">Para Birimi</Label>
                      <Input
                        type="select"
                        className="form-select"
                        id="currency"
                        {...validation.getFieldProps('currency')}
                        invalid={validation.touched.currency && validation.errors.currency}
                      >
                        <option value="TL">TL</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </Input>
                      {validation.touched.currency && validation.errors.currency && (
                        <FormFeedback type="invalid" tooltip>{validation.errors.currency}</FormFeedback>
                      )}
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Tedarikçi</h4>
                    <div className="mb-3">
                      <Label htmlFor="supplier" className="form-label">Tedarikçi Seçin</Label>
                      <Select
                        id="supplier"
                        value={selectedSupplier}
                        onChange={setSelectedSupplier}
                        options={supplierOptions}
                        className="react-select"
                        classNamePrefix="select"
                      />
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Ek Bilgiler</h4>
                    <div className="mb-3">
                      <Label htmlFor="additional_information" className="form-label">Ek Açıklamalar</Label>
                      <Input
                        type="textarea"
                        className="form-control"
                        id="additional_information"
                        rows="3"
                        placeholder="Enter additional information"
                        {...validation.getFieldProps('additional_information')}
                      />
                    </div>
                  </CardBody>
                </Card>
                <Col>
                  <div className="d-grid gap-2">
                    <button type="submit" size="lg" className="btn btn-primary">
                      Ürün Ekle
                    </button>
                  </div>
                </Col>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProductAdd;